import { difference } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { StressIncrementType } from '../../../../../Generated/Commodity-Management-Api';
import { StoneXDivider, StoneXRow } from '../../../../StoneX';
import { StoneXButton, StoneXSelect, StoneXTextField } from '../../../../StoneXMui';
import { StoneXTransferList } from '../../../../StoneXMui/StoneXTransferList/StoneXTransferList';
import { ExposureColumns, ExposureReportColumn, ExposureReportConfiguration, USER_SELECTABLE_COLUMNS } from '../../Reports/Reports/Exposure/Types';
import { BaseReportSettingsPageProps } from './types';

const DEFAULT_EXPOSURE_COLUMNS = USER_SELECTABLE_COLUMNS;

function isExposureColumn(column: string): column is ExposureReportColumn {
  return USER_SELECTABLE_COLUMNS.includes(column as ExposureReportColumn);
}

function getUniqueKnownExposureColumns(columns: string[]): ExposureReportColumn[] {
  const seen = new Set<ExposureReportColumn>();
  return columns.reduce((result, column) => {
    if (!isExposureColumn(column) || seen.has(column)) {
      return result;
    }

    seen.add(column);
    result.push(column);
    return result;
  }, [] as ExposureReportColumn[]);
}

function parseExposureReportConfiguration(configurationJson: string): ExposureReportConfiguration {
  return JSON.parse(configurationJson) as ExposureReportConfiguration;
}

function getInitialAvailableColumns(configuration: ExposureReportConfiguration): ExposureReportColumn[] {
  if (!configuration.columns?.available?.length) {
    return DEFAULT_EXPOSURE_COLUMNS;
  }

  const sanitized = getUniqueKnownExposureColumns(configuration.columns.available);
  return sanitized.length ? sanitized : DEFAULT_EXPOSURE_COLUMNS;
}

function getInitialActiveColumns(configuration: ExposureReportConfiguration, availableColumns: ExposureReportColumn[]): ExposureReportColumn[] {
  const configuredOrDefault = configuration.columns?.active?.length
    ? getUniqueKnownExposureColumns(configuration.columns.active)
    : availableColumns;

  return configuredOrDefault.filter((column) => availableColumns.includes(column));
}

export default function ExposureReportSettingsPage({ report, isSaving, onDeleteReport, onUpdateReport }: BaseReportSettingsPageProps) {
  const stressIncrementTypes = useMemo<StressIncrementType[]>(() => [StressIncrementType.Percent, StressIncrementType.Price], []);

  const initialConfiguration = useMemo(() => parseExposureReportConfiguration(report.configuration), [report.configuration]);
  const initialAvailableColumns = useMemo(() => getInitialAvailableColumns(initialConfiguration), [initialConfiguration]);
  const initialActiveColumns = useMemo(
    () => getInitialActiveColumns(initialConfiguration, initialAvailableColumns),
    [initialConfiguration, initialAvailableColumns],
  );

  const [reportName, setReportName] = useState<string>(report.reportName);
  const [configuration, setConfiguration] = useState<ExposureReportConfiguration>(initialConfiguration);

  const [availableColumns, setAvailableColumns] = useState<ExposureReportColumn[]>(initialAvailableColumns);
  const [unavailableColumns, setUnavailableColumns] = useState<ExposureReportColumn[]>(difference(DEFAULT_EXPOSURE_COLUMNS, initialAvailableColumns));
  const [activeColumns, setActiveColumns] = useState<ExposureReportColumn[]>(initialActiveColumns);
  const [inactiveAvailableColumns, setInactiveAvailableColumns] = useState<ExposureReportColumn[]>(difference(initialAvailableColumns, initialActiveColumns));

  const [stressIncrementType, setStressIncrementType] = useState<StressIncrementType>(initialConfiguration.behavior?.stressIncrementType ?? StressIncrementType.Percent);
  const [stressIncrementsText, setStressIncrementsText] = useState<string>((initialConfiguration.behavior?.stressIncrements ?? [5, 10, 15]).join(', '));

  const exposureColumnOrderMap = useMemo<{ [key: string]: number }>(() => {
    const keys = Object.keys(ExposureColumns);
    const map: { [key: string]: number } = {};
    keys.forEach((value: string, index) => (map[value] = index));
    return map;
  }, []);

  useEffect(() => {
    const parsed = parseExposureReportConfiguration(report.configuration);
    const currentAvailableColumns = getInitialAvailableColumns(parsed);
    const currentUnavailableColumns = difference(DEFAULT_EXPOSURE_COLUMNS, currentAvailableColumns);
    const currentActiveColumns = getInitialActiveColumns(parsed, currentAvailableColumns);
    const currentInactiveAvailableColumns = difference(currentAvailableColumns, currentActiveColumns);

    setReportName(report.reportName);
    setConfiguration(parsed);
    setAvailableColumns(currentAvailableColumns);
    setUnavailableColumns(currentUnavailableColumns);
    setActiveColumns(currentActiveColumns);
    setInactiveAvailableColumns(currentInactiveAvailableColumns);
    setStressIncrementType(parsed.behavior?.stressIncrementType ?? StressIncrementType.Percent);
    setStressIncrementsText((parsed.behavior?.stressIncrements ?? [5, 10, 15]).join(', '));
  }, [report]);

  function onAvailableColumnsUpdate(updatedUnavailable: ExposureReportColumn[], updatedAvailable: ExposureReportColumn[]) {
    const filteredActiveColumns = activeColumns.filter((column) => updatedAvailable.includes(column));
    const updatedInactiveAvailableColumns = difference(updatedAvailable, filteredActiveColumns);

    setUnavailableColumns(updatedUnavailable);
    setAvailableColumns(updatedAvailable);
    setActiveColumns(filteredActiveColumns);
    setInactiveAvailableColumns(updatedInactiveAvailableColumns);
  }

  function onActiveColumnsUpdate(updatedInactiveAvailable: ExposureReportColumn[], updatedActive: ExposureReportColumn[]) {
    setInactiveAvailableColumns(updatedInactiveAvailable);
    setActiveColumns(updatedActive);
  }

  function resetConfiguration() {
    const parsed = parseExposureReportConfiguration(report.configuration);
    const currentAvailableColumns = getInitialAvailableColumns(parsed);
    const currentUnavailableColumns = difference(DEFAULT_EXPOSURE_COLUMNS, currentAvailableColumns);
    const currentActiveColumns = getInitialActiveColumns(parsed, currentAvailableColumns);
    const currentInactiveAvailableColumns = difference(currentAvailableColumns, currentActiveColumns);

    setReportName(report.reportName);
    setConfiguration(parsed);
    setAvailableColumns(currentAvailableColumns);
    setUnavailableColumns(currentUnavailableColumns);
    setActiveColumns(currentActiveColumns);
    setInactiveAvailableColumns(currentInactiveAvailableColumns);
    setStressIncrementType(parsed.behavior?.stressIncrementType ?? StressIncrementType.Percent);
    setStressIncrementsText((parsed.behavior?.stressIncrements ?? [5, 10, 15]).join(', '));
  }

  function saveConfiguration() {
    const stressIncrements = stressIncrementsText
      .split(',')
      .map((value) => Number(value.trim()))
      .filter((value) => !Number.isNaN(value));

    const nextConfiguration: ExposureReportConfiguration = {
      ...configuration,
      behavior: {
        ...configuration.behavior,
        stressIncrementType,
        stressIncrements,
      },
      columns: {
        available: availableColumns,
        active: activeColumns,
      },
    };

    onUpdateReport({
      ...report,
      reportName: reportName.trim(),
      configuration: JSON.stringify(nextConfiguration),
    });

    setConfiguration(nextConfiguration);
  }

  return (
    <div>
      <StoneXRow>
        <StoneXTextField value={reportName} label="Report Name" onUpdate={setReportName} />
      </StoneXRow>

      <StoneXDivider />

      <StoneXRow>
        <h3>Stress Scenario</h3>
      </StoneXRow>

      <StoneXRow align='start'>
        <div style={{ flex: '1 1' }}>
          <StoneXRow>
            <StoneXSelect
              label="Stress Increment Type"
              options={stressIncrementTypes}
              getId={(x) => x}
              getOptionLabel={(x) => x}
              value={stressIncrementType}
              onChange={(value) => setStressIncrementType(value ?? StressIncrementType.Percent)}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXTextField
              label="Stress Increments"
              helperText="Comma-separated values, e.g. 5, 10, 15"
              value={stressIncrementsText}
              onUpdate={setStressIncrementsText}
            />
          </StoneXRow>
        </div>
        <div style={{ flex: '1 1' }} />
      </StoneXRow>

      <StoneXDivider />

      <StoneXRow>
        <h3>Columns</h3>
      </StoneXRow>

      <StoneXRow align='start'>
        <div style={{ flex: '1 1' }}>
          <p>Step 1: Choose which columns are available to the customer.</p>
          <StoneXTransferList
            leftList={unavailableColumns}
            leftHeader="Unavailable Columns"
            rightList={availableColumns}
            rightHeader="Available Columns"
            getId={(x) => ExposureColumns[x]?.colId ?? x}
            getSortingOrder={(x) => exposureColumnOrderMap[x]}
            getOptionLabel={(x) => ExposureColumns[x]?.headerName ?? x}
            onUpdate={onAvailableColumnsUpdate}
          />
        </div>

        <div style={{ flex: '1 1' }}>
          <p>Step 2: From available columns, choose which ones are active by default.</p>
          <StoneXTransferList
            leftList={inactiveAvailableColumns}
            leftHeader="Available but Inactive"
            rightList={activeColumns}
            rightHeader="Active Columns"
            getId={(x) => ExposureColumns[x]?.colId ?? x}
            getSortingOrder={(x) => exposureColumnOrderMap[x]}
            getOptionLabel={(x) => ExposureColumns[x]?.headerName ?? x}
            onUpdate={onActiveColumnsUpdate}
          />
        </div>
      </StoneXRow>

      <StoneXRow justify="end" marginDirection="top">
        <StoneXButton variant="outlined" onClick={() => onDeleteReport(report.customerReportId)}>
          Delete Report
        </StoneXButton>
        <StoneXButton variant="outlined" onClick={resetConfiguration}>Reset</StoneXButton>
        <StoneXButton isWorking={isSaving} onClick={saveConfiguration}>Save</StoneXButton>
      </StoneXRow>
    </div>
  );
}
